import { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Play, Trash2, Save, Download, ChevronRight, ArrowLeft,
  Rows3, Type as TypeIcon, Sigma, Layers, Sliders, Calendar, Send, Bell,
} from "lucide-react";
import { useCurrencyRail } from "@/context/CurrencyRailContext";

const DATE_PRESETS = [
  { value: "this_month",    label: "This month" },
  { value: "last_month",    label: "Last month" },
  { value: "this_quarter",  label: "This quarter" },
  { value: "last_quarter",  label: "Last quarter" },
  { value: "this_year",     label: "This year" },
  { value: "last_year",     label: "Last year" },
  { value: "mtd",           label: "MTD" },
  { value: "ytd",           label: "YTD" },
  { value: "custom",        label: "Custom range" },
];

const COMPARISONS = [
  { value: "none",          label: "No comparison" },
  { value: "prior_period",  label: "vs. prior period" },
  { value: "prior_year",    label: "vs. same period last year" },
];

const SECTION_KIND_META = {
  header:     { icon: TypeIcon, label: "Header",     hint: "Label-only row (no math)" },
  type:       { icon: Layers,   label: "Type total", hint: "Sum by transaction type" },
  categories: { icon: Rows3,    label: "Categories", hint: "Sum matching categories" },
  accounts:   { icon: Sliders,  label: "Accounts",   hint: "Sum matching bank/wallet accounts" },
  subtotal:   { icon: Sigma,    label: "Subtotal",   hint: "Sum of data rows above" },
};

function fm(n) {
  if (n === null || n === undefined) return "";
  const v = Number(n);
  return v < 0 ? `-$${Math.abs(v).toFixed(2)}` : `$${v.toFixed(2)}`;
}

export default function ReportDesigner() {
  const [view, setView] = useState("list"); // "list" | "edit" | "run"
  const [editingId, setEditingId] = useState(null);
  const [runningId, setRunningId] = useState(null);

  const openEditor = (id = null) => { setEditingId(id); setView("edit"); };
  const openRunner = (id) => { setRunningId(id); setView("run"); };
  const backToList = () => { setEditingId(null); setRunningId(null); setView("list"); };

  return (
    <AppShell>
      <div className="mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Analyze</div>
        <h1 className="font-display text-4xl mt-2">Report Designer</h1>
        <p className="text-sm text-[var(--muted-text)] mt-1 max-w-xl">
          Build the exact report you need — pick sections, categories, accounts, date range, save it once, and re-run any time.
        </p>
      </div>
      {view === "list" && <ReportList onNew={() => openEditor(null)} onEdit={openEditor} onRun={openRunner} />}
      {view === "edit" && <ReportEditor id={editingId} onBack={backToList} onSaved={(t) => openRunner(t.id)} />}
      {view === "run"  && <ReportRunner id={runningId} onBack={backToList} onEdit={() => openEditor(runningId)} />}
    </AppShell>
  );
}

// ================== List view ==================
function ReportList({ onNew, onEdit, onRun }) {
  const [rows, setRows] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [scheduleCount, setScheduleCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const [{ data: r }, { data: s }] = await Promise.all([
        api.get("/reports/designer/templates"),
        api.get("/reports/designer/schedules"),
      ]);
      setRows(r);
      setScheduleCount(s.length);
    } catch { toast.error("Couldn't load reports"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/reports/designer/templates/${deleteTarget.id}`);
      toast.success("Report deleted");
      setDeleteTarget(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="report-designer-list">
      <div className="flex items-center gap-3">
        <div className="text-sm text-[var(--muted-text)]">
          {rows.length} saved report{rows.length === 1 ? "" : "s"}
        </div>
        <div className="flex-1" />
        <Button variant="outline" className="h-10" data-testid="rd-manage-schedules-btn"
                onClick={() => setManageOpen(true)}>
          <Bell className="w-4 h-4 mr-2" /> Schedules
          {scheduleCount > 0 && <span className="ml-1.5 text-[11px] px-1.5 py-0.5 rounded-full bg-[var(--brand-light)] text-[var(--brand-text)]">{scheduleCount}</span>}
        </Button>
        <Button className="btn-brand h-10" data-testid="rd-new-btn" onClick={onNew}>
          <Plus className="w-4 h-4 mr-2" /> New report
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="card-flat p-12 text-center">
          <div className="text-sm text-[var(--muted-text)] mb-4">No custom reports yet. Try one of these starters:</div>
          <div className="flex flex-wrap gap-2 justify-center">
            <Button variant="outline" data-testid="rd-quickstart-marketing"
                    onClick={() => onNew()}>
              Start blank
            </Button>
          </div>
        </div>
      ) : (
        <div className="card-flat overflow-hidden">
          <table className="qb-table w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--border)]">
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Period</th>
                <th className="px-4 py-2 font-medium">Comparison</th>
                <th className="px-4 py-2 font-medium">Sections</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`rd-row-${r.id}`}>
                  <td className="px-4 py-2.5 font-medium">
                    <div>{r.name}</div>
                    {r.description && <div className="text-xs text-[var(--muted-text)] truncate max-w-[380px]">{r.description}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-xs">{DATE_PRESETS.find(d => d.value === r.date_preset)?.label || r.date_preset}</td>
                  <td className="px-4 py-2.5 text-xs">{COMPARISONS.find(c => c.value === r.comparison)?.label || r.comparison}</td>
                  <td className="px-4 py-2.5 text-xs text-[var(--muted-text)]">{r.sections?.length || 0}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" data-testid={`rd-run-${r.id}`} onClick={() => onRun(r.id)}>
                        <Play className="w-4 h-4 mr-1" /> Run
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`rd-edit-${r.id}`} onClick={() => onEdit(r.id)}>Edit</Button>
                      <Button size="icon" variant="ghost" data-testid={`rd-delete-${r.id}`}
                              onClick={() => setDeleteTarget(r)}>
                        <Trash2 className="w-4 h-4 text-[var(--expense)]" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the saved report only. Your transactions and books are untouched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="rd-delete-confirm" onClick={remove}
                               className="bg-[var(--expense)] hover:opacity-90 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ManageSchedulesDialog open={manageOpen} onClose={() => setManageOpen(false)}
                              templates={rows} onChange={load} />
    </div>
  );
}

// ================== Editor view ==================
const BLANK = {
  name: "",
  description: "",
  base: "custom",
  sections: [
    { kind: "header", label: "Revenue", value: null },
    { kind: "type",   label: "Total income", value: "income" },
    { kind: "subtotal", label: "Subtotal — Revenue", value: null },
    { kind: "header", label: "Expenses", value: null },
    { kind: "type",   label: "Total expenses", value: "expense" },
    { kind: "subtotal", label: "Subtotal — Expenses", value: null },
  ],
  date_preset: "this_month",
  comparison: "none",
};

// Stable client-side ID stamped on each section so React reconciles reorders
// correctly (sections have no natural ID until saved).
let _uidCounter = 0;
const nextUid = () => `s${Date.now().toString(36)}${++_uidCounter}`;
const withUids = (sections) => sections.map((s) => (s._uid ? s : { ...s, _uid: nextUid() }));

function ReportEditor({ id, onBack, onSaved }) {
  const [tpl, setTpl] = useState(() => ({ ...BLANK, sections: withUids(BLANK.sections) }));
  const { activeRail } = useCurrencyRail();
  const railPayload = activeRail === "USD" || activeRail === "NGN"
    ? { currency: activeRail }
    : {};
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: cats }, { data: accs }] = await Promise.all([
          api.get("/categories"),
          api.get("/accounts"),
        ]);
        setCategories(cats.map((c) => c.name || c));
        setAccounts(accs);
      } catch (e) {
        // Non-blocking: category/account pickers stay empty, section labels
        // still editable. Log at debug so first-time onboarding flakiness
        // is traceable without user-visible noise.
        console.debug("report editor picker load failed:", e?.message);
      }
      if (id) {
        try {
          const { data } = await api.get(`/reports/designer/templates/${id}`);
          setTpl({
            name: data.name,
            description: data.description || "",
            base: data.base || "custom",
            sections: withUids(data.sections || []),
            date_preset: data.date_preset || "this_month",
            comparison: data.comparison || "none",
          });
        } catch { toast.error("Couldn't load report"); }
      }
    })();
  }, [id]);

  const patchSection = (idx, patch) => {
    const next = [...tpl.sections];
    next[idx] = { ...next[idx], ...patch };
    setTpl({ ...tpl, sections: next });
  };
  const moveSection = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= tpl.sections.length) return;
    const next = [...tpl.sections];
    [next[idx], next[j]] = [next[j], next[idx]];
    setTpl({ ...tpl, sections: next });
  };
  const removeSection = (idx) => {
    setTpl({ ...tpl, sections: tpl.sections.filter((_, i) => i !== idx) });
  };
  const addSection = (kind) => {
    const defaults = {
      header:     { kind, label: "New header", value: null },
      type:       { kind, label: "Income total", value: "income" },
      categories: { kind, label: "Selected categories", value: [] },
      accounts:   { kind, label: "Selected accounts", value: [] },
      subtotal:   { kind, label: "Subtotal", value: null },
    };
    setTpl({ ...tpl, sections: [...tpl.sections, { ...defaults[kind], _uid: nextUid() }] });
  };

  const save = async () => {
    if (!tpl.name.trim()) { toast.error("Name is required"); return; }
    if (!tpl.sections.length) { toast.error("Add at least one section"); return; }
    setSaving(true);
    try {
      // Strip client-only _uid before sending — the backend Section model
      // rejects unknown fields.
      const clean = tpl.sections.map(({ _uid, ...rest }) => rest);
      const body = { ...tpl, sections: clean, name: tpl.name.trim(), description: tpl.description.trim() };
      const { data } = id
        ? await api.patch(`/reports/designer/templates/${id}`, body)
        : await api.post("/reports/designer/templates", body);
      toast.success(id ? "Report updated" : "Report saved");
      onSaved?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const runPreview = async () => {
    if (!tpl.sections.length) { toast.error("Add at least one section"); return; }
    setPreviewing(true);
    try {
      const clean = tpl.sections.map(({ _uid, ...rest }) => rest);
      const { data } = await api.post("/reports/designer/run", {
        template: { ...tpl, sections: clean, name: tpl.name || "Preview" },
        ...railPayload,
      });
      setPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally { setPreviewing(false); }
  };

  return (
    <div className="space-y-4" data-testid="report-designer-editor">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="rd-editor-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1" />
        <Button variant="outline" onClick={runPreview} disabled={previewing} data-testid="rd-editor-preview">
          <Play className="w-4 h-4 mr-1" /> {previewing ? "Running…" : "Preview"}
        </Button>
        <Button className="btn-brand" onClick={save} disabled={saving} data-testid="rd-editor-save">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : id ? "Save changes" : "Save report"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="card-flat p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Report name</Label>
                <Input data-testid="rd-editor-name" value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })}
                       placeholder="e.g. Marketing ROI" className="mt-1 h-10" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Base</Label>
                <Select value={tpl.base} onValueChange={(v) => setTpl({ ...tpl, base: v })}>
                  <SelectTrigger data-testid="rd-editor-base" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Custom (blank)</SelectItem>
                    <SelectItem value="pnl">P&L (Income Statement)</SelectItem>
                    <SelectItem value="balance_sheet">Balance Sheet</SelectItem>
                    <SelectItem value="cash_flow">Cash Flow</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Description</Label>
                <Textarea data-testid="rd-editor-desc" value={tpl.description} onChange={(e) => setTpl({ ...tpl, description: e.target.value })}
                          rows={2} placeholder="Optional notes for your team" className="mt-1" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Period</Label>
                <Select value={tpl.date_preset} onValueChange={(v) => setTpl({ ...tpl, date_preset: v })}>
                  <SelectTrigger data-testid="rd-editor-period" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DATE_PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Comparison</Label>
                <Select value={tpl.comparison} onValueChange={(v) => setTpl({ ...tpl, comparison: v })}>
                  <SelectTrigger data-testid="rd-editor-compare" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COMPARISONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="card-flat overflow-hidden" data-testid="rd-editor-sections">
            <div className="px-4 py-2.5 border-b border-[var(--border)] flex items-center gap-2">
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Sections</div>
              <div className="flex-1" />
              {Object.entries(SECTION_KIND_META).map(([k, m]) => {
                const Icon = m.icon;
                return (
                  <Button key={k} variant="outline" size="sm" data-testid={`rd-add-${k}`} onClick={() => addSection(k)}>
                    <Icon className="w-3.5 h-3.5 mr-1" /> {m.label}
                  </Button>
                );
              })}
            </div>
            {tpl.sections.length === 0 ? (
              <div className="p-8 text-center text-sm text-[var(--muted-text)]">Add a section to get started.</div>
            ) : (
              <div>
                {tpl.sections.map((s, i) => (
                  <SectionRow key={s._uid} idx={i} section={s} onChange={(p) => patchSection(i, p)}
                              onMove={(d) => moveSection(i, d)} onRemove={() => removeSection(i)}
                              categories={categories} accounts={accounts} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Live preview panel */}
        <div className="lg:col-span-1">
          <div className="card-flat p-4 sticky top-4">
            <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-2">Preview</div>
            {!preview ? (
              <div className="text-xs text-[var(--muted-text)]">Click Preview to compute this report against your books.</div>
            ) : <RunResult data={preview} compact />}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionRow({ idx, section, onChange, onMove, onRemove, categories, accounts }) {
  const meta = SECTION_KIND_META[section.kind] || SECTION_KIND_META.header;
  const Icon = meta.icon;
  return (
    <div className="px-4 py-3 border-b border-[var(--divider)] last:border-none" data-testid={`rd-section-${idx}`}>
      <div className="flex items-start gap-3">
        <div className="flex flex-col gap-0.5 pt-0.5">
          <button onClick={() => onMove(-1)} className="text-[var(--muted-text)] hover:text-[var(--brand)] text-xs" data-testid={`rd-section-up-${idx}`}>▲</button>
          <button onClick={() => onMove(1)}  className="text-[var(--muted-text)] hover:text-[var(--brand)] text-xs" data-testid={`rd-section-down-${idx}`}>▼</button>
        </div>
        <div className="w-6 shrink-0 pt-1"><Icon className="w-4 h-4 text-[var(--brand)]" /></div>
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-12 gap-2">
          <div className="sm:col-span-3">
            <Select value={section.kind} onValueChange={(v) => onChange({ kind: v, value: v === "categories" || v === "accounts" ? [] : v === "type" ? "income" : null })}>
              <SelectTrigger className="h-9" data-testid={`rd-section-kind-${idx}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(SECTION_KIND_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Input data-testid={`rd-section-label-${idx}`}
                 value={section.label} onChange={(e) => onChange({ label: e.target.value })}
                 placeholder="Row label" className="sm:col-span-5 h-9" />
          <div className="sm:col-span-4">
            {section.kind === "type" && (
              <Select value={section.value || "income"} onValueChange={(v) => onChange({ value: v })}>
                <SelectTrigger className="h-9" data-testid={`rd-section-type-${idx}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                  <SelectItem value="saving">Saving</SelectItem>
                </SelectContent>
              </Select>
            )}
            {section.kind === "categories" && (
              <MultiPicker testid={`rd-section-cats-${idx}`}
                           value={section.value || []}
                           options={categories.map((c) => ({ value: c, label: c }))}
                           onChange={(v) => onChange({ value: v })} placeholder="Add categories" />
            )}
            {section.kind === "accounts" && (
              <MultiPicker testid={`rd-section-accs-${idx}`}
                           value={section.value || []}
                           options={accounts.map((a) => ({ value: a.id, label: `${a.name}${a.institution_name ? " · " + a.institution_name : ""}` }))}
                           onChange={(v) => onChange({ value: v })} placeholder="Add accounts" />
            )}
            {(section.kind === "header" || section.kind === "subtotal") && (
              <div className="text-[11px] text-[var(--muted-text)] pt-2">{meta.hint}</div>
            )}
          </div>
        </div>
        <Button size="icon" variant="ghost" data-testid={`rd-section-remove-${idx}`} onClick={onRemove}>
          <Trash2 className="w-4 h-4 text-[var(--expense)]" />
        </Button>
      </div>
    </div>
  );
}

function MultiPicker({ value = [], options, onChange, placeholder, testid }) {
  const [addOpen, setAddOpen] = useState(false);
  const remaining = options.filter((o) => !value.includes(o.value));
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={testid}>
      {value.map((v) => {
        const opt = options.find((o) => o.value === v);
        return (
          <span key={v} className="text-[11px] px-2 py-0.5 rounded-full bg-[var(--brand-light)] text-[var(--brand-text)] inline-flex items-center gap-1">
            {opt?.label || v}
            <button onClick={() => onChange(value.filter((x) => x !== v))} className="hover:text-[var(--expense)]">×</button>
          </span>
        );
      })}
      {remaining.length > 0 && (
        <Select value="" onValueChange={(v) => { if (v) onChange([...value, v]); setAddOpen(false); }}>
          <SelectTrigger className="h-7 w-auto min-w-[120px] text-xs" data-testid={`${testid}-add`}>
            <SelectValue placeholder={placeholder || "Add"} />
          </SelectTrigger>
          <SelectContent>
            {remaining.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

// ================== Runner view ==================
function ReportRunner({ id, onBack, onEdit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const { activeRail } = useCurrencyRail();
  const railPayload = activeRail === "USD" || activeRail === "NGN"
    ? { currency: activeRail }
    : {};

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: r }] = await Promise.all([
        api.get(`/reports/designer/templates/${id}`),
        api.post("/reports/designer/run", { template_id: id, ...railPayload }),
      ]);
      setMeta(t);
      setData(r);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Run failed");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activeRail]);

  useEffect(() => { if (id) run(); }, [id, run]);

  const exportCsv = () => {
    if (!data) return;
    const hasCompare = data.comparison !== "none";
    const rows = [
      hasCompare ? ["Row", "Current", "Previous", "Δ", "%"] : ["Row", "Amount"],
      ...data.rows.map((r) => hasCompare
        ? [r.label,
           r.amount === null ? "" : r.amount,
           r.previous === null || r.previous === undefined ? "" : r.previous,
           r.delta === null || r.delta === undefined ? "" : r.delta,
           r.pct === null || r.pct === undefined ? "" : r.pct]
        : [r.label, r.amount === null ? "" : r.amount]),
    ];
    const csv = rows.map((r) => r.map((v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(meta?.name || "report").replace(/[^a-z0-9]+/gi, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="report-designer-runner">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="rd-runner-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="text-sm text-[var(--muted-text)]">
          {meta?.name || "…"} <ChevronRight className="w-3 h-3 inline mx-0.5" /> {data ? `${data.period.start} → ${data.period.end}` : ""}
        </div>
        <div className="flex-1" />
        <Button variant="outline" onClick={run} disabled={loading} data-testid="rd-runner-refresh">
          <Play className="w-4 h-4 mr-1" /> {loading ? "Running…" : "Re-run"}
        </Button>
        <Button variant="outline" onClick={exportCsv} disabled={!data} data-testid="rd-runner-export">
          <Download className="w-4 h-4 mr-1" /> Export CSV
        </Button>
        <Button variant="outline" onClick={() => setScheduleOpen(true)} data-testid="rd-runner-schedule">
          <Calendar className="w-4 h-4 mr-1" /> Schedule
        </Button>
        <Button className="btn-brand" onClick={onEdit} data-testid="rd-runner-edit">Edit</Button>
      </div>

      {loading && !data ? (
        <div className="card-flat p-10 text-center text-sm text-[var(--muted-text)]">Running…</div>
      ) : data ? (
        <RunResult data={data} />
      ) : null}

      <ScheduleDialog open={scheduleOpen} onClose={() => setScheduleOpen(false)}
                       templateId={id} templateName={meta?.name || "Report"} />
    </div>
  );
}

function RunResult({ data, compact }) {
  const hasCompare = data.comparison && data.comparison !== "none";
  return (
    <div className="card-flat overflow-hidden" data-testid="rd-result">
      {!compact && (
        <div className="px-5 py-2.5 border-b border-[var(--divider)] text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
          {data.name} · {data.period.start} → {data.period.end}
          {hasCompare && data.prior ? ` · vs ${data.prior.start} → ${data.prior.end}` : ""}
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--divider)]">
            <th className="text-left px-4 py-1.5">Row</th>
            <th className="text-right px-4 py-1.5">Current</th>
            {hasCompare && <th className="text-right px-4 py-1.5">Previous</th>}
            {hasCompare && <th className="text-right px-4 py-1.5">Δ</th>}
            {hasCompare && <th className="text-right px-4 py-1.5">%</th>}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => {
            // Rows come from the server in a fixed template order — combine
            // kind+label+i so keys stay stable when the report re-runs but are
            // still unique across duplicate labels.
            const rowKey = `${r.kind || "row"}:${r.label || ""}:${i}`;
            if (r.kind === "header") {
              return (
                <tr key={rowKey} className="bg-[var(--hover)]/50">
                  <td colSpan={hasCompare ? 5 : 2} className="px-4 py-1.5 text-xs uppercase tracking-widest text-[var(--muted-text)]">
                    {r.label}
                  </td>
                </tr>
              );
            }
            const isSubtotal = r.kind === "subtotal";
            return (
              <tr key={rowKey} className={`border-t border-[var(--divider)] ${isSubtotal ? "font-semibold bg-[var(--brand-light)]/40" : ""}`}
                  data-testid={`rd-result-row-${i}`}>
                <td className={`px-4 py-1.5 ${isSubtotal ? "" : "pl-6 text-[var(--muted-text)]"}`}>{r.label}</td>
                <td className="px-4 py-1.5 text-right font-mono">{fm(r.amount)}</td>
                {hasCompare && <td className="px-4 py-1.5 text-right font-mono text-[var(--muted-text)]">{fm(r.previous)}</td>}
                {hasCompare && <td className={`px-4 py-1.5 text-right font-mono ${r.delta > 0 ? "text-[#2F6B4A]" : r.delta < 0 ? "text-[#B4283A]" : ""}`}>{fm(r.delta)}</td>}
                {hasCompare && <td className={`px-4 py-1.5 text-right font-mono text-xs ${r.pct > 0 ? "text-[#2F6B4A]" : r.pct < 0 ? "text-[#B4283A]" : ""}`}>{r.pct === null || r.pct === undefined ? "—" : `${r.pct}%`}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


// ================== Schedule dialog (create/edit one schedule for a template) ==================
const WEEKDAYS = [
  { value: 0, label: "Monday" }, { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" }, { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" }, { value: 5, label: "Saturday" }, { value: 6, label: "Sunday" },
];

function ScheduleDialog({ open, onClose, templateId, templateName, existing, onSaved }) {
  const tzGuess = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
  })();

  const blank = useMemo(() => ({
    frequency: "weekly", day_of_week: 0, day_of_month: 1,
    hour: 8, timezone_name: tzGuess,
    recipients: "", format: "pdf", enabled: true,
  }), [tzGuess]);

  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setForm({
        frequency: existing.frequency || "weekly",
        day_of_week: existing.day_of_week ?? 0,
        day_of_month: existing.day_of_month ?? 1,
        hour: existing.hour ?? 8,
        timezone_name: existing.timezone_name || tzGuess,
        recipients: (existing.recipients || []).join(", "),
        format: existing.format || "pdf",
        enabled: existing.enabled !== false,
      });
    } else {
      setForm(blank);
    }
  }, [open, existing, blank, tzGuess]);

  const build = () => {
    const recipients = form.recipients.split(",").map((s) => s.trim()).filter(Boolean);
    return {
      frequency: form.frequency,
      day_of_week: form.frequency === "weekly" ? Number(form.day_of_week) : null,
      day_of_month: form.frequency === "monthly" ? Number(form.day_of_month) : null,
      hour: Number(form.hour),
      timezone_name: form.timezone_name || "UTC",
      recipients,
      format: form.format,
      enabled: form.enabled,
    };
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = build();
      const { data } = existing
        ? await api.patch(`/reports/designer/schedules/${existing.id}`, body)
        : await api.post(`/reports/designer/templates/${templateId}/schedule`, body);
      toast.success(existing ? "Schedule updated" : "Report scheduled");
      onSaved?.(data);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const sendNow = async () => {
    if (!existing) {
      // create-then-send so user can test the setup
      setSending(true);
      try {
        const { data } = await api.post(`/reports/designer/templates/${templateId}/schedule`, build());
        await api.post(`/reports/designer/schedules/${data.id}/send-now`);
        toast.success("Sent — check your inbox in ~30 seconds");
        onSaved?.(data);
        onClose?.();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Send failed");
      } finally { setSending(false); }
      return;
    }
    setSending(true);
    try {
      await api.post(`/reports/designer/schedules/${existing.id}/send-now`);
      toast.success("Sent — check your inbox in ~30 seconds");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="rd-schedule-dialog">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit schedule" : `Schedule "${templateName}"`}</DialogTitle>
          <DialogDescription>
            We'll email this report on the cadence you pick. Turn it off any time — nothing else changes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Cadence</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger className="h-10 mt-1" data-testid="rd-schedule-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.frequency === "weekly" && (
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Day</Label>
                <Select value={String(form.day_of_week)} onValueChange={(v) => setForm({ ...form, day_of_week: Number(v) })}>
                  <SelectTrigger className="h-10 mt-1" data-testid="rd-schedule-dow"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WEEKDAYS.map((d) => <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.frequency === "monthly" && (
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Day of month</Label>
                <Input type="number" min={1} max={31} value={form.day_of_month} data-testid="rd-schedule-dom"
                       onChange={(e) => setForm({ ...form, day_of_month: e.target.value })}
                       className="h-10 mt-1" />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Send at (hour)</Label>
              <Input type="number" min={0} max={23} value={form.hour} data-testid="rd-schedule-hour"
                     onChange={(e) => setForm({ ...form, hour: e.target.value })}
                     className="h-10 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Timezone</Label>
              <Input value={form.timezone_name} data-testid="rd-schedule-tz"
                     onChange={(e) => setForm({ ...form, timezone_name: e.target.value })}
                     placeholder="e.g. America/New_York"
                     className="h-10 mt-1 font-mono text-xs" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Recipients</Label>
            <Textarea rows={2} data-testid="rd-schedule-recipients"
                      value={form.recipients}
                      onChange={(e) => setForm({ ...form, recipients: e.target.value })}
                      placeholder="alice@acme.co, bob@acme.co"
                      className="mt-1 text-sm" />
            <div className="text-[11px] text-[var(--muted-text)] mt-1">Comma-separated. At least one required.</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Attachment format</Label>
              <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}>
                <SelectTrigger className="h-10 mt-1" data-testid="rd-schedule-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-end pt-6">
              <label className="text-xs text-[var(--muted-text)] flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid="rd-schedule-enabled"
                       checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
                Enabled
              </label>
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center pt-3">
          <Button variant="ghost" onClick={sendNow} disabled={sending || saving} data-testid="rd-schedule-send-now">
            <Send className="w-4 h-4 mr-1" /> {sending ? "Sending…" : "Send test now"}
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} data-testid="rd-schedule-cancel">Cancel</Button>
            <Button className="btn-brand" onClick={save} disabled={saving} data-testid="rd-schedule-save">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : existing ? "Save changes" : "Create schedule"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ================== Manage schedules dialog (list + edit + delete + send-now) ==================
function ManageSchedulesDialog({ open, onClose, templates, onChange }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // schedule row
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reports/designer/schedules");
      setRows(data);
    } catch { toast.error("Couldn't load schedules"); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const templateName = (tid) => templates?.find((t) => t.id === tid)?.name || "(deleted report)";

  const toggle = async (r) => {
    try {
      await api.patch(`/reports/designer/schedules/${r.id}`, { enabled: !r.enabled });
      load(); onChange?.();
    } catch { toast.error("Couldn't update"); }
  };

  const sendNow = async (r) => {
    try {
      await api.post(`/reports/designer/schedules/${r.id}/send-now`);
      toast.success("Sent");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
  };

  const remove = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/reports/designer/schedules/${confirmDel.id}`);
      toast.success("Schedule removed");
      setConfirmDel(null);
      load(); onChange?.();
    } catch { toast.error("Delete failed"); }
  };

  const describe = (r) => {
    const at = `${String(r.hour).padStart(2, "0")}:00 ${r.timezone_name || "UTC"}`;
    if (r.frequency === "daily") return `Every day at ${at}`;
    if (r.frequency === "weekly") return `Every ${WEEKDAYS.find(w => w.value === r.day_of_week)?.label || "week"} at ${at}`;
    if (r.frequency === "monthly") return `Day ${r.day_of_month} of each month at ${at}`;
    return "";
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent className="max-w-3xl" data-testid="rd-manage-schedules">
          <DialogHeader>
            <DialogTitle>Scheduled reports</DialogTitle>
            <DialogDescription>
              Every schedule runs on your books at the chosen cadence and emails the result to the recipients you set.
            </DialogDescription>
          </DialogHeader>
          {rows.length === 0 ? (
            <div className="text-center text-sm text-[var(--muted-text)] p-6">
              No schedules yet. Open any saved report and hit <b>Schedule</b> to set one up.
            </div>
          ) : (
            <table className="w-full text-sm mt-2">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--divider)]">
                  <th className="py-1.5 pl-1">Report</th>
                  <th className="py-1.5">Cadence</th>
                  <th className="py-1.5">To</th>
                  <th className="py-1.5 text-center">Enabled</th>
                  <th className="py-1.5 text-right pr-1">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`rd-schedule-row-${r.id}`} className="border-b border-[var(--divider)] last:border-none">
                    <td className="py-2 pl-1">
                      <div className="font-medium">{r.template_name || templateName(r.template_id)}</div>
                      <div className="text-[10px] text-[var(--muted-text)]">
                        {r.last_run ? `Last: ${r.last_run.slice(0, 10)} · ${r.last_status || "—"}` : "Never run"}
                      </div>
                    </td>
                    <td className="py-2 text-xs">{describe(r)}</td>
                    <td className="py-2 text-xs">
                      {r.recipients?.slice(0, 2).join(", ")}
                      {r.recipients?.length > 2 && ` +${r.recipients.length - 2}`}
                    </td>
                    <td className="py-2 text-center">
                      <label className="inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={r.enabled} onChange={() => toggle(r)}
                               data-testid={`rd-schedule-toggle-${r.id}`} />
                      </label>
                    </td>
                    <td className="py-2 text-right pr-1">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" data-testid={`rd-schedule-sendnow-${r.id}`} onClick={() => sendNow(r)}>
                          <Send className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`rd-schedule-edit-${r.id}`} onClick={() => setEditing(r)}>
                          Edit
                        </Button>
                        <Button size="icon" variant="ghost" data-testid={`rd-schedule-del-${r.id}`} onClick={() => setConfirmDel(r)}>
                          <Trash2 className="w-4 h-4 text-[var(--expense)]" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>

      {editing && (
        <ScheduleDialog open={!!editing} onClose={() => setEditing(null)}
                         templateId={editing.template_id} templateName={editing.template_name || ""}
                         existing={editing} onSaved={() => { setEditing(null); load(); onChange?.(); }} />
      )}

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this schedule?</AlertDialogTitle>
            <AlertDialogDescription>The report template itself stays — only the recurring delivery is removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="rd-schedule-delete-confirm" onClick={remove}
                               className="bg-[var(--expense)] hover:opacity-90 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

